import { ReactNode, useState } from 'react'
import { Check } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CharacterClassIcon } from 'app/frontend/base/components'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getAllSpecializations } from 'app/frontend/base/services/characterValues'
import { CharacterClass } from 'app/frontend/types/custom'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { cn } from '@/lib/utils'
import { ResolvedAssignmentParticipant } from './eventActivityTimelineResolver'
import { EncounterAssignmentEntityOverride } from './eventActivityPlanOverrides'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { useTranslation } from 'react-i18next'
import { getAssignmentEntityLabel } from '../../TeamPlans/components/assignmentEntityLabels'

type AggregateOverrideEntity = Extract<
  RaidPlanAssignmentEntity,
  { kind: 'everyone' | 'collection' | 'group' }
>

export type AggregateOption = {
  entity: AggregateOverrideEntity
}

const specializationById = getAllSpecializations()

const aggregateEntityKey = (entity: AggregateOverrideEntity) => {
  if (entity.kind === 'everyone') return 'everyone'
  if (entity.kind === 'collection') return `collection:${entity.collection}`
  return `group:${entity.group}`
}

export const defaultAggregateOptions: AggregateOption[] = [
  { entity: { kind: 'everyone' } },
  { entity: { kind: 'collection', collection: 'healers' } },
  { entity: { kind: 'collection', collection: 'ranged' } },
  { entity: { kind: 'collection', collection: 'melee' } },
  { entity: { kind: 'collection', collection: 'tanks' } },
  { entity: { kind: 'collection', collection: 'dps' } },
  ...Array.from({ length: 8 }, (_, index) => ({
    entity: {
      kind: 'group' as const,
      group: (index + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
    },
  })),
]

type Props = {
  trigger: ReactNode
  participants: ResolvedAssignmentParticipant[]
  aggregateOptions: AggregateOption[]
  selectedOverride?: EncounterAssignmentEntityOverride
  onSelect: (override: EncounterAssignmentEntityOverride) => void
  disabled?: boolean
}

const EventPlanOverridePopover = ({
  trigger,
  participants,
  aggregateOptions,
  selectedOverride,
  onSelect,
  disabled,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Popover open={disabled ? false : open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='inline-flex cursor-pointer disabled:cursor-default'
          disabled={disabled}
        >
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-fit p-0' align='start'>
        <Command>
          <CommandList>
            <CommandEmpty>No options found</CommandEmpty>
            <CommandGroup heading='Individuals'>
              {participants.map((participant) => {
                const specialization = participant.specId
                  ? specializationById[participant.specId.toString()]
                  : undefined
                const isSelected =
                  selectedOverride?.kind === 'participant' &&
                  participant.characterId === selectedOverride.characterId
                return (
                  <CommandItem
                    key={participant.participantId}
                    onSelect={() => {
                      onSelect({
                        kind: 'participant',
                        characterId: participant.characterId,
                      })
                      setOpen(false)
                    }}
                    className='flex items-center gap-2'
                  >
                    {specialization ? (
                      <BlizzardIcon
                        iconName={specialization.icon}
                        size='small'
                        className='shrink-0'
                      />
                    ) : (
                      <CharacterClassIcon
                        characterClass={
                          participant.characterClass as CharacterClass
                        }
                        size='small'
                      />
                    )}
                    <span
                      className={classColours[participant.characterClass].text}
                    >
                      {participant.name}
                    </span>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
            <CommandGroup heading='Groups'>
              {aggregateOptions.map((option) => {
                const isSelected =
                  selectedOverride?.kind === 'aggregate' &&
                  aggregateEntityKey(selectedOverride.entity) ===
                    aggregateEntityKey(option.entity)
                return (
                  <CommandItem
                    key={aggregateEntityKey(option.entity)}
                    onSelect={() => {
                      onSelect({
                        kind: 'aggregate',
                        entity: option.entity,
                      })
                      setOpen(false)
                    }}
                    className='flex items-center gap-2'
                  >
                    <span className='text-foreground'>
                      {getAssignmentEntityLabel(t, option.entity)}
                    </span>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default EventPlanOverridePopover
